import React, { CSSProperties } from 'react';
import type { WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { TypedMemo } from '../../../../common/types';
import styles from './WaveformGlyph.module.scss';

/** The heights (0..1) of the seven bars at rest; the animation scales each one. */
const BARS = [0.45, 0.8, 0.6, 1, 0.7, 0.9, 0.5];
/** The zigzag repeats every 16 px (four points); it is drawn 64 px wide and slides left by one period. */
const LINE_LEVELS = [8, 20, 10, 18];
const LINE_PATH = `M${Array.from({ length: 18 }, (_, index) => `${index * 4 - 4},${LINE_LEVELS[index % 4]}`).join(' L')}`;

interface WaveformGlyphProps {
  shape: WaveformShape;
  /** The colour of the drawing: the accent of the song theme. Absent: the colour of the text. */
  color?: string;
}

/**
 * A 48 × 28 px live drawing of a waveform shape (owner wish 2026-10-08): bars, a line, a ring, or a
 * dashed line for none. It moves gently, and stays still with reduced motion. The label next to it
 * names the shape, so the drawing is hidden from screen readers.
 */
const WaveformGlyphComponent: React.FC<WaveformGlyphProps> = ({ shape, color }) => {
  const style = color === undefined ? undefined : ({ '--wave': color } as CSSProperties);
  return (
    <svg
      className={styles.glyph}
      style={style}
      viewBox="0 0 48 28"
      aria-hidden="true"
      focusable="false"
      data-testid={`waveform-glyph-${shape}`}
    >
      {shape === 'bars' &&
        BARS.map((level, index) => {
          const height = 24 * level;
          return (
            <rect
              // eslint-disable-next-line react/no-array-index-key -- a fixed list of bars
              key={index}
              className={styles.bar}
              x={3 + index * 6.4}
              y={14 - height / 2}
              width={3.6}
              height={height}
              rx={1.8}
              style={{ animationDelay: `${-0.17 * index}s` }}
            />
          );
        })}
      {shape === 'line' && (
        <g className={styles.lineMove}>
          <path className={styles.lineGlow} d={LINE_PATH} />
          <path className={styles.line} d={LINE_PATH} />
        </g>
      )}
      {shape === 'ring' && (
        <>
          <circle className={styles.ringFill} cx={24} cy={14} r={8} />
          <circle className={styles.ringEcho} cx={24} cy={14} r={10} />
          <circle className={styles.ring} cx={24} cy={14} r={8} />
        </>
      )}
      {shape === 'none' && <line className={styles.none} x1={8} y1={14} x2={40} y2={14} />}
    </svg>
  );
};

export const WaveformGlyph = TypedMemo(WaveformGlyphComponent);
